/**
 * Convierte el análisis de un chat de WhatsApp (esquema propio de
 * whatsapp_chat_service.py) al mismo esquema de "extraction" que usa el
 * flujo de Texto del DJ Master (dj_assistant.py). Esto permite reutilizar
 * EXACTAMENTE la misma UI de revisión ítem-por-ítem (ClientTextImportDialog)
 * y el mismo endpoint /apply-client-text-extraction, de forma que la
 * información de WhatsApp acabe en los mismos sitios estructurados del
 * evento (momentos_especiales_evento, notas, tareas, canciones...) en vez
 * de en campos sueltos "_whatsapp" de solo lectura.
 *
 * Todo texto pasa por toDisplayText() porque el LLM de WhatsApp a veces
 * devuelve objetos donde se esperan strings (bug ya conocido con chats
 * largos/adjuntos) — evita reintroducir el crash de React #31.
 */
import { toDisplayText } from './safeRenderText';

const NAMED_SONGS = [
  ['cancion_primer_baile', 'baile_novios', 'Primer baile'],
  ['cancion_entrada_novia', 'entrada', 'Entrada novia'],
  ['cancion_entrada_novio', 'entrada', 'Entrada novio'],
  ['cancion_entrada_conjunta', 'entrada', 'Entrada conjunta'],
  ['cancion_tarta', 'otro', 'Corte de tarta'],
  ['cancion_padre_hija', 'otro', 'Baile padre-hija'],
  ['cancion_madre_hijo', 'otro', 'Baile madre-hijo'],
];

// Mapeo de claves de horario de WhatsApp -> claves conocidas por el backend
// de Texto (cocktail/ceremonia/banquete/fiesta_baile/fin_fiesta). El resto
// de horarios de WhatsApp (montaje, tarta, otros...) no tienen hueco en ese
// esquema fijo, así que se vuelcan como notas técnicas individuales para no
// perder información, igual de revisables que cualquier otra nota.
const HORARIO_MAP = {
  ceremonia: 'ceremonia',
  coctel: 'cocktail',
  banquete: 'banquete',
  inicio_baile: 'fiesta_baile',
  fin_fiesta: 'fin_fiesta',
};

export function mapWhatsAppAnalysisToExtraction(analysis) {
  const a = analysis || {};
  const notas = [];
  const pushNota = (text) => {
    const t = toDisplayText(text).trim();
    if (t) notas.push(t);
  };

  // Momentos especiales narrativos del chat de WhatsApp (bingo, "no habrá baile
  // nupcial"...) — se preparan primero (con su propio bucket de canciones) para
  // poder enganchar ahí abajo las canciones de canciones_solicitadas cuyo
  // "momento" coincida con uno de ellos, en vez de dejarlas sueltas como
  // imperdibles (si no, al desmarcar el momento la canción se "colaría" igual).
  const waMomentos = a.momentos_especiales || [];
  const normalizeText = (s) => toDisplayText(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const significantWords = (s) => normalizeText(s).split(/[^a-z0-9]+/).filter((w) => w.length >= 4);
  const waMomentoWordSets = waMomentos.map((m) => {
    if (!m) return [];
    const text = typeof m === 'string' ? m : (m.descripcion || m.cuando || m.notas || '');
    return significantWords(text);
  });
  const momentoSongBuckets = waMomentos.map(() => []);
  // Comparación por palabras significativas en vez de substring literal: el
  // "momento" de una canción ("cierre de fiesta") y la descripción del momento
  // narrativo ("Cierre de la fiesta con la canción Happy") casi nunca coinciden
  // letra a letra por culpa de palabras de relleno ("de la"), pero sí comparten
  // palabras clave ("cierre", "fiesta").
  const matchWaMomentoIndex = (momentoText) => {
    const needleWords = significantWords(momentoText);
    if (needleWords.length === 0) return -1;
    return waMomentoWordSets.findIndex((words) => words.length > 0 && needleWords.some((w) => words.includes(w)));
  };

  // Canciones generales solicitadas -> imperdibles, SALVO que su "momento"
  // coincida con un momento narrativo del chat, en cuyo caso se enganchan a
  // ese momento (así, si se desmarca el momento, la canción desaparece entera).
  const canciones_imperdibles = [];
  (a.canciones_solicitadas || []).forEach((c) => {
    const titulo = typeof c === 'string' ? toDisplayText(c) : toDisplayText(c.titulo);
    if (!titulo) return;
    const artista = typeof c === 'string' ? null : (c.artista ? toDisplayText(c.artista) : null);
    const momentoText = typeof c === 'string' ? null : c.momento;
    const waIdx = matchWaMomentoIndex(momentoText);
    if (waIdx >= 0) {
      momentoSongBuckets[waIdx].push({ titulo, artista, rol: 'principal' });
    } else {
      canciones_imperdibles.push({ titulo, artista, contexto: momentoText ? toDisplayText(momentoText) : null });
    }
  });

  // Vetos: canciones vetadas + géneros vetados (prefijados para distinguirlos)
  const canciones_prohibidas = [
    ...(a.canciones_vetadas || []).map((c) => ({ titulo: toDisplayText(c), artista: null, motivo: null })),
    ...(a.generos_vetados || []).map((g) => ({ titulo: `Género: ${toDisplayText(g)}`, artista: null, motivo: 'género vetado' })),
  ].filter((c) => c.titulo);

  const gustos_musicales = (a.generos_preferidos || []).map((g) => toDisplayText(g)).filter(Boolean);

  // Momentos especiales: canciones nombradas (primer baile, entrada...) +
  // momentos narrativos propios del chat de WhatsApp (con sus canciones
  // enganchadas arriba, si las hubo).
  const momentos_especiales = [];
  NAMED_SONGS.forEach(([field, tipo, label]) => {
    const song = a[field];
    if (song && (song.titulo || typeof song === 'string')) {
      const titulo = typeof song === 'string' ? toDisplayText(song) : toDisplayText(song.titulo);
      if (!titulo) return;
      momentos_especiales.push({
        tipo,
        titulo: label,
        descripcion: '',
        cuando: song.momento ? toDisplayText(song.momento) : null,
        participantes: null,
        frase_dj: null,
        canciones: [{ titulo, artista: song.artista ? toDisplayText(song.artista) : null, rol: 'principal' }],
      });
    }
  });
  waMomentos.forEach((m, idx) => {
    if (!m) return;
    // Evitar volcar TODOS los campos del objeto en el título si falta
    // 'descripcion' (eso mezclaría cuando/participantes/notas en una sola
    // frase ilegible) — se usa solo el texto más relevante disponible.
    let descripcion;
    if (typeof m === 'string') {
      descripcion = toDisplayText(m);
    } else if (m.descripcion) {
      descripcion = toDisplayText(m.descripcion);
    } else {
      descripcion = toDisplayText(m.notas || m.cuando || m.participantes || '');
    }
    if (!descripcion) return;
    momentos_especiales.push({
      tipo: 'otro',
      titulo: descripcion,
      descripcion: typeof m === 'object' ? toDisplayText(m.notas || '') : '',
      cuando: m?.cuando ? toDisplayText(m.cuando) : null,
      participantes: m?.participantes ? toDisplayText(m.participantes) : null,
      frase_dj: null,
      canciones: momentoSongBuckets[idx],
    });
  });

  // Horarios: los 5 campos conocidos se mapean directo; el resto se vuelca
  // como notas técnicas individuales (revisables igualmente).
  const horarios = {};
  const waHorarios = a.horarios || {};
  Object.entries(HORARIO_MAP).forEach(([waKey, textoKey]) => {
    if (waHorarios[waKey]) horarios[textoKey] = toDisplayText(waHorarios[waKey]);
  });
  if (waHorarios.llegada_montaje) pushNota(`🚚 Montaje: ${toDisplayText(waHorarios.llegada_montaje)}`);
  if (waHorarios.tarta) pushNota(`🎂 Tarta (hora): ${toDisplayText(waHorarios.tarta)}`);
  if (waHorarios.primer_baile) pushNota(`💃 Primer baile (hora): ${toDisplayText(waHorarios.primer_baile)}`);
  (waHorarios.otros || []).forEach((o) => pushNota(`🕐 ${toDisplayText(o.momento || o)}: ${toDisplayText(o.hora || '')}`));

  // Resto de información del chat sin hueco propio en el esquema de Texto ->
  // notas técnicas individuales, cada una revisable/editable por separado.
  (a.notas_importantes || []).forEach((n) => pushNota(n));
  (a.peticiones_especiales || []).forEach((p) => pushNota(`📝 Petición: ${toDisplayText(p)}`));
  (a.restricciones || []).forEach((r) => pushNota(`⚠️ Restricción: ${toDisplayText(r)}`));
  if (a.logistica && typeof a.logistica === 'object') {
    const { parking, montaje, corriente, otros } = a.logistica;
    if (parking) pushNota(`🚗 Parking: ${toDisplayText(parking)}`);
    if (montaje) pushNota(`🔧 Montaje: ${toDisplayText(montaje)}`);
    if (corriente) pushNota(`🔌 Corriente: ${toDisplayText(corriente)}`);
    if (otros) pushNota(`ℹ️ Logística: ${toDisplayText(otros)}`);
  }
  if (a.fotomaton && (a.fotomaton.contratado || a.fotomaton.tipo)) {
    const f = a.fotomaton;
    const parts = [f.tipo, f.fondo && `fondo: ${f.fondo}`, f.props && `props: ${f.props}`, f.ubicacion && `ubicación: ${f.ubicacion}`, f.horario && `horario: ${f.horario}`]
      .map((p) => (p ? toDisplayText(p) : '')).filter(Boolean);
    pushNota(`📸 Fotomatón: ${parts.join(', ')}`);
  }
  (a.servicios_extra || []).forEach((s) => pushNota(typeof s === 'string' ? `🎪 ${toDisplayText(s)}` : `🎪 ${toDisplayText(s.servicio)}: ${toDisplayText(s.detalles)}`));
  (a.contactos || []).forEach((c) => pushNota(`👤 ${toDisplayText(c.rol)}: ${toDisplayText(c.nombre)}${c.telefono ? ` (${toDisplayText(c.telefono)})` : ''}${c.email ? ` - ${toDisplayText(c.email)}` : ''}`));
  if (a.precio_acordado) pushNota(`💰 Precio acordado: ${toDisplayText(a.precio_acordado)}`);
  if (a.forma_pago) pushNota(`💳 Forma de pago: ${toDisplayText(a.forma_pago)}`);
  if (a.senal_anticipo) pushNota(`💵 Señal/Anticipo: ${toDisplayText(a.senal_anticipo)}`);
  (a.transcripciones_audios || []).forEach((t) => pushNota(`🎤 Audio: ${toDisplayText(t)}`));

  return {
    canciones_imperdibles,
    canciones_prohibidas,
    gustos_musicales,
    playlist_spotify_url: null,
    momentos_especiales,
    horarios,
    notas_tecnicas: notas,
    tareas_pendientes: [],
    briefing_ia: toDisplayText(a.resumen_detallado || a.resumen || ''),
    tono_cliente: null,
  };
}
